import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchTasks,
  createTask,
  updateTask,
  completeTask,
  fetchHousekeepingStatus,
  updateUnitHousekeepingStatus,
  fetchHousekeepingStaff,
  batchAssignHousekeeping,
  type TasksQuery,
  type CreateTaskPayload,
  type UpdateTaskPayload,
} from '@/services/api/operations.api';

export function useTasks(query?: TasksQuery) {
  return useQuery({
    queryKey: ['tasks', query],
    queryFn: () => fetchTasks(query),
    staleTime: 30_000,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTaskPayload) => createTask(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTaskPayload }) =>
      updateTask(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useCompleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => completeTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useHousekeepingStatus(unitId: string | undefined) {
  return useQuery({
    queryKey: ['housekeeping', unitId],
    queryFn: () => fetchHousekeepingStatus(unitId!),
    enabled: !!unitId,
    staleTime: 30_000,
  });
}

export function useHousekeepingStaff(propertyId?: string) {
  return useQuery({
    queryKey: ['housekeeping-staff', propertyId],
    queryFn: () => fetchHousekeepingStaff(propertyId),
    staleTime: 60_000,
  });
}

export function useBatchAssignHousekeeping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { taskIds: string[]; assigneeId: string }) =>
      batchAssignHousekeeping(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useUpdateHousekeepingStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, status }: { unitId: string; status: string }) =>
      updateUnitHousekeepingStatus(unitId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['housekeeping'] });
    },
  });
}
